/**
 * videoFileStore.js
 *
 * Filesystem-backed staging for large offline videos (multi-GB GoPro clips).
 *
 * On native (Capacitor) we stage videos on disk and reference them by path in
 * the offline queue instead of holding a multi-GB Blob in IndexedDB / the
 * WebView JS heap. This is what makes multi-GB "cache on the water, upload at
 * the ramp" reliable:
 *   - downloadToFile(): streams an HTTP(S) URL straight to disk (no memory
 *     materialization) — used for GoPro clips which come from a local URL.
 *   - getFileBlob():    returns a *disk-backed* Blob (via convertFileSrc +
 *     fetch) that r2Video can slice() for multipart upload without loading the
 *     whole file into the JS heap.
 *   - deleteFile():     removes the staged file after a confirmed upload.
 *
 * On web (non-native) these helpers are no-ops / unsupported and callers fall
 * back to the existing IndexedDB-blob path in offlineStore.js.
 */

import { Capacitor } from "@capacitor/core";

export const IS_NATIVE = Capacitor?.isNativePlatform?.() === true;

// App-private, persistent directory. Filesystem plugin is dynamically imported
// so the web bundle never pulls it in unless used.
const SUBDIR = "the99/pending-videos";

let _fsModule = null;
async function fsMod() {
  if (_fsModule) return _fsModule;
  _fsModule = await import("@capacitor/filesystem");
  return _fsModule;
}

function relPath(certId) {
  return `${SUBDIR}/${certId}.bin`;
}

/**
 * Stream a URL to disk without materializing it in memory. Returns a file
 * reference { path, directory, uri, size } suitable for queuePendingVideoFile.
 * Native only. Throws on web.
 */
export async function downloadToFile({ certId, url, onProgress }) {
  if (!IS_NATIVE) throw new Error("downloadToFile is native-only");
  if (!certId || !url) throw new Error("downloadToFile requires certId and url");
  const { Filesystem, Directory } = await fsMod();
  const path = relPath(certId);

  // Progress events (available on newer plugin versions). Best-effort.
  let progressHandle = null;
  if (onProgress && Filesystem.addListener) {
    try {
      progressHandle = await Filesystem.addListener("progress", (e) => {
        if (e && typeof e.bytes === "number" && typeof e.contentLength === "number") {
          onProgress(e.bytes, e.contentLength);
        }
      });
    } catch {
      /* listener unsupported — ignore */
    }
  }

  try {
    await Filesystem.downloadFile({
      url,
      path,
      directory: Directory.Data,
      recursive: true,
      progress: !!onProgress,
    });
  } finally {
    if (progressHandle && progressHandle.remove) {
      try { await progressHandle.remove(); } catch { /* ignore */ }
    }
  }

  const size = await getFileSize({ path, directory: "DATA" }).catch(() => 0);
  return { path, directory: "DATA", size };
}

/** Return the native file:// (or capacitor) URI for a staged file. */
export async function getFileUri({ path }) {
  const { Filesystem, Directory } = await fsMod();
  const { uri } = await Filesystem.getUri({ path, directory: Directory.Data });
  return uri;
}

/** Return the on-disk byte size of a staged file (0 if unknown/missing). */
export async function getFileSize({ path }) {
  if (!IS_NATIVE) return 0;
  try {
    const { Filesystem, Directory } = await fsMod();
    const stat = await Filesystem.stat({ path, directory: Directory.Data });
    return Number(stat?.size) || 0;
  } catch {
    return 0;
  }
}

/**
 * Return a disk-backed Blob for a staged file. Chromium (Android WebView)
 * backs large Blobs on disk, so fetch()+blob() here does NOT load the whole
 * file into the JS heap, and blob.slice() reads lazily for multipart upload.
 * Native only.
 */
export async function getFileBlob({ path }) {
  if (!IS_NATIVE) throw new Error("getFileBlob is native-only");
  const uri = await getFileUri({ path });
  const webUrl = Capacitor.convertFileSrc(uri);
  const resp = await fetch(webUrl);
  if (!resp.ok) throw new Error(`Failed to read staged file (${resp.status})`);
  return resp.blob();
}

/** Delete a staged file. Best-effort; never throws. */
export async function deleteFile({ path }) {
  if (!IS_NATIVE || !path) return;
  try {
    const { Filesystem, Directory } = await fsMod();
    await Filesystem.deleteFile({ path, directory: Directory.Data });
  } catch {
    /* already gone / best-effort */
  }
}
